import React from 'react';
import './turnover-invoice-list-item-card.scss';
import {
  TurnOverInvoiceListItem,
  TurnOverInvoiceValidationStatus,
} from '@zonofi/zono-money-store';
import { TouchableOpacity } from '../../../common';
import { ReactComponent as InvoiceIcon } from '@zonofi/svg-icons/invoice-icon.svg';
import { getFormattedAmount } from '@zonofi/common';

interface TurnoverInvoiceListItemCardProps {
  invoice: TurnOverInvoiceListItem;
  onPress?: (invoice: TurnOverInvoiceListItem) => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const VALIDATION_STATUS_STYLES: Record<
  TurnOverInvoiceValidationStatus,
  { className: string; label: string }
> = {
  'Parsing the invoice': { className: 'status-parsing', label: 'Parsing...' },
  'Parsing Failed': { className: 'status-failed', label: 'Parsing Failed' },
  'Distributor Not Found': {
    className: 'status-not-found',
    label: 'Distributor Not Found',
  },
  'Buyer Not Found': {
    className: 'status-not-found',
    label: 'Buyer Not Found',
  },
  'Invoice Data Incomplete': {
    className: 'status-warning',
    label: 'Data Incomplete',
  },
  'Duplicate Invoice': {
    className: 'status-failed',
    label: 'Duplicate Invoice',
  },
  'Products Unmapped': {
    className: 'status-warning',
    label: 'Products Unmapped',
  },
  'Partially Mapped': {
    className: 'status-warning',
    label: 'Partially Mapped',
  },
  'Likely Fraudulent': {
    className: 'status-failed',
    label: 'Likely Fraudulent',
  },
  Successful: { className: 'status-success', label: 'Parsing Successful' },
};

const isParsingFailed = (
  status: TurnOverInvoiceValidationStatus | null
): boolean => {
  return status === 'Parsing Failed' || status === 'Parsing the invoice';
};

export const TurnoverInvoiceListItemCard: React.FC<
  TurnoverInvoiceListItemCardProps
> = ({ invoice, onPress, isFirst = false, isLast = false }) => {
  const {
    invoiceNumber,
    invoiceDate,
    validationStatus,
    submittedByName,
    submittedOn,
    distributorName,
    buyerName,
    invoiceAmount,
    contributionAmount,
    contributionSkus,
  } = invoice;

  const statusConfig = validationStatus
    ? VALIDATION_STATUS_STYLES[validationStatus]
    : null;

  const isParseFailed = isParsingFailed(validationStatus);

  const handlePress = () => {
    onPress?.(invoice);
  };

  return (
    <TouchableOpacity className={`turnover-invoice-card`} onPress={handlePress}>
      {/* Header Row */}
      <div className="invoice-card-header">
        <div className="invoice-card-icon-title">
          <div className="invoice-icon-wrapper">
            <InvoiceIcon width={36} height={36} />
          </div>
          <div className="invoice-title-block">
            <span className="invoice-number font-semibold">
              {invoiceNumber}
            </span>
            <span className="invoice-date font-semibold">
              Invoiced on {invoiceDate}
            </span>
          </div>
        </div>

        {statusConfig && (
          <span
            className={`invoice-validation-badge font-semibold ${statusConfig.className}`}
          >
            {statusConfig.label}
          </span>
        )}
      </div>

      {/* Amount Row */}
      <div className="invoice-amounts-row">
        <div className="invoice-amount-item">
          <span className="amount-label font-semibold">Invoice Amt.</span>
          <span className="amount-value font-semibold">
            {isParseFailed ? '-' : getFormattedAmount(invoiceAmount)}
          </span>
        </div>
        <div className="invoice-amount-item">
          <span className="amount-label font-semibold">Contribution Amt.</span>
          <span className="amount-value font-semibold">
            {isParseFailed ? '-' : getFormattedAmount(contributionAmount)}
          </span>
        </div>
        <div className="invoice-amount-item">
          <span className="amount-label font-semibold">Contribution SKU's</span>
          <span className="amount-value font-semibold">
            {isParseFailed || contributionSkus == null ? '-' : contributionSkus}
          </span>
        </div>
      </div>

      {/* Details Row */}
      <div className="invoice-details-row">
        <div className="invoice-detail-line">
          <span className="detail-key font-semibold">Submitted By</span>
          <span className="detail-value font-regular">
            {submittedByName ? `${submittedByName} on ${submittedOn}` : '-'}
          </span>
        </div>
        <div className="invoice-detail-line">
          <span className="detail-key font-semibold">Distributor</span>
          <span className="detail-value font-regular">
            {distributorName || '-'}
          </span>
        </div>
        <div className="invoice-detail-line">
          <span className="detail-key font-semibold">Buyer</span>
          <span className="detail-value font-regular">{buyerName || '-'}</span>
        </div>
      </div>
    </TouchableOpacity>
  );
};
